import Button from '@clayui/button';
import React, { useState } from 'react';
import { primaryButtonStyle } from '../../utils/commonUtil';
import Loader from '../../generic-components/loader/Loader';
import { useForm } from 'react-hook-form';
import ClayLayout from '@clayui/layout';
import FormComponent from '../../generic-components/formComponent/FormComponent';
import { pitchFormBusinessDetailsFields } from '../../utils/PitchFormConfig';
import { defaultValueForSelect, scopeKeyPitchForm } from '../../utils/Config';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { updatePitchFormValues } from '../../slices/pitchFormSlice';
import { useEffect } from 'react';
import { useFetchListTypeEntries } from '../../services/ListTypeEntryService';
import { convertFilesToBase64 } from '../../utils/FileToBase64';
import { toast } from 'react-toastify';
import { useApiMutation } from '../../apis/useApi';

interface IPitchFormBusinessDetail {
    setStep: React.Dispatch<React.SetStateAction<number>>;
    setResStatusCode: React.Dispatch<React.SetStateAction<number>>;
    setResStatusMessage: React.Dispatch<React.SetStateAction<string>>;
}
const PitchFormBusinessDetail: React.FC<IPitchFormBusinessDetail> = ({
    setStep,
    setResStatusCode,
    setResStatusMessage
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const pitchFormData = useSelector((state: RootState) => state.pitchForm);
    const {
        control,
        register,
        formState: { errors: formErrors },
        watch,
        handleSubmit,
        setError,
        getValues,
        reset,
        setValue
    } = useForm<any>({
        mode: 'all',
        defaultValues: { ...pitchFormData }
    });
    const dispatch = useDispatch();
    const dubaiChamberScopeKey = 'Dubai Chamber Intranet';
    const country = useFetchListTypeEntries('COUNTRY');
    const targetMarket = useFetchListTypeEntries('TARGET_MARKETS', 'name:asc');
    const channels = useFetchListTypeEntries('EXPANSION_BUSINESS_PURPOSE');
    const [uploadExpansionDocument, setUploadExpansionDocument] = useState<
        File[]
    >(pitchFormData?.expansionDocument || []);
    const marketEntryChannelValue = watch('marketEntryChannel');
    console.log('uploadExpansionDocument', uploadExpansionDocument);
    const fileHandling = {
        expansionDocument: {
            selectedFiles: uploadExpansionDocument,
            setSelectedFiles: setUploadExpansionDocument
        }
    };

    const managedFields = React.useMemo(
        () => [...pitchFormBusinessDetailsFields.map((f) => f.name)],
        []
    );

    const getManagedValues = (currentValues: any) => {
        const payload: any = {};
        managedFields.forEach((name) => {
            if (currentValues[name] !== undefined) {
                payload[name] = currentValues[name];
            }
        });
        payload.expansionDocument = uploadExpansionDocument;
        return payload;
    };

    useEffect(() => {
        return () => {
            const values = getManagedValues(getValues());
            dispatch(updatePitchFormValues(values));
        };
    }, [dispatch, getValues, managedFields, uploadExpansionDocument]);
    const dropdownOptions = {
        currentInternationalMarket: country,
        targetMarkets: targetMarket,
        marketEntryChannel: channels
    };

    const submitDataHandler = useApiMutation<any, {}>(
        `o/c/pitchforminquiries/scopes/${scopeKeyPitchForm}`,
        'post',
        {
            onSuccess: async (data) => {
                if (data && data?.id) {
                    setIsLoading(false);
                    // Success - redirect to final screen with success
                    setStep(3);
                    setResStatusMessage(
                        'Your inquiry has been submitted successfully!'
                    );
                    setResStatusCode(200);
                    toast.success(
                        'Your inquiry has been submitted successfully!',
                        {
                            theme: 'light',
                            autoClose: 2000
                        }
                    );
                } else {
                    setStep(3);
                    setResStatusMessage(
                        data?.status?.statusMessage || 'Something went wrong'
                    );
                    toast.error(
                        data?.status?.statusMessage || 'Something went wrong',
                        {
                            theme: 'light',
                            autoClose: 4000
                        }
                    );
                    setIsLoading(false);
                }
                setTimeout(() => {
                    window.scrollTo(0, 0);
                }, 1000);
            },
            onError: (error: any) => {
                setStep(3);
                toast.error('Submission failed. Please try again.', {
                    theme: 'light',
                    autoClose: 4000
                });
            }
        }
    );

    const onSubmit = async (data: any) => {
        setIsLoading(true);
        console.log('data', data);
        const {
            companyProfile,
            expansionDocument,
            industry,
            industrySubcategory,
            step,
            industryOptions,
            subIndustryOptions,
            ...rest
        } = data;
        dispatch(
            updatePitchFormValues({
                ...data,
                expansionDocument: uploadExpansionDocument
            })
        );
        const companyProfileDoc = await convertFilesToBase64(companyProfile);
        const expansionDocumentDoc = await convertFilesToBase64(
            uploadExpansionDocument
        );
        const industryNames = (Array.isArray(industry) ? industry : [industry])
            .map(
                (id) =>
                    data?.industryOptions.find(
                        (opt: any) => opt.key.toString() === id.toString()
                    )?.name
            )
            .filter(Boolean)
            .join(', ');

        const subIndustryNames = (
            Array.isArray(industrySubcategory)
                ? industrySubcategory
                : [industrySubcategory]
        )
            .map(
                (id) =>
                    data?.subIndustryOptions.find(
                        (opt: any) => opt.key.toString() === id.toString()
                    )?.name
            )
            .filter(Boolean)
            .join(', ');

        console.log('companyProfileDoc', companyProfileDoc);
        console.log('expansionDocumentDoc', expansionDocumentDoc);
        let payload = {
            ...rest,
            businessSector: industryNames,
            businessSubSector: subIndustryNames,
            companyProfileAttachment: companyProfileDoc?.length
                ? {
                      fileBase64: companyProfileDoc[0].fileBase64,
                      name: companyProfileDoc[0].name
                  }
                : null,
            pitchFormToLeadAttachment: expansionDocumentDoc.map((doc: any) => ({
                key: 'expansionDocument',
                document: {
                    fileBase64: doc.fileBase64,
                    name: doc.name
                },
                ...doc
            }))
        };
        console.log('payload', payload);
        try {
            submitDataHandler.mutate({
                data: payload,
                params: {
                    p_auth: (window as any)?.Liferay?.authToken
                }
            });
        } catch (error) {
            console.error('Submission error:', error);
        }
    };
    const newPitchFormBusinessDetailsFields =
        pitchFormBusinessDetailsFields?.filter((field) => {
            if (field.name === 'otherMarketEntryChannel') {
                return marketEntryChannelValue?.includes('others');
            }
            return true;
        });
    return (
        <>
            {isLoading && <Loader />}
            <form onSubmit={handleSubmit(onSubmit)}>
                <ClayLayout.Row
                    justify="start"
                    className="basic_detail_form mt-4"
                >
                    {newPitchFormBusinessDetailsFields?.map((field) => {
                        const colSize = ['expansionDetails']?.includes(
                            field?.name
                        )
                            ? 12
                            : 6;
                        return (
                            <ClayLayout.Col
                                key={field.name}
                                xl={colSize}
                                lg={colSize}
                                md={colSize}
                                sm={colSize}
                                xs={12}
                            >
                                <FormComponent
                                    {...field}
                                    register={register}
                                    control={control}
                                    errors={formErrors}
                                    watch={watch}
                                    setError={setError}
                                    errorTitle={field.errorMessage}
                                    options={
                                        (dropdownOptions as any)[field.name] ||
                                        []
                                    }
                                    selectedFiles={
                                        fileHandling[
                                            field.name as keyof typeof fileHandling
                                        ]?.selectedFiles
                                    }
                                    setSelectedFiles={
                                        fileHandling[
                                            field.name as keyof typeof fileHandling
                                        ]?.setSelectedFiles
                                    }
                                />
                            </ClayLayout.Col>
                        );
                    })}
                </ClayLayout.Row>
                <div className="basic-details-button-section">
                    <Button
                        type="submit"
                        className="bottom-cut"
                        style={primaryButtonStyle}
                    >
                        <span className="bottom-cut-span"></span>
                        Submit
                    </Button>
                </div>
            </form>
        </>
    );
};

export default PitchFormBusinessDetail;
